import api from "@/lib/api";

// Web App Badge API — shows the unread count on the installed PWA icon
// (Android/desktop Chromium, iOS 16.4+). No-op where unsupported.
export function syncAppBadge(unread) {
  try {
    if (!("setAppBadge" in navigator)) return;
    if (unread > 0) navigator.setAppBadge(unread).catch(() => {});
    else if ("clearAppBadge" in navigator) navigator.clearAppBadge().catch(() => {});
  } catch { /* unsupported */ }
}

// Fire a device notification through the registered service worker (installed PWA),
// falling back to a plain Notification. Only call when permission === "granted".
export async function deviceNotify(title, body) {
  try {
    const options = {
      body,
      icon: "/android-chrome-192x192.png",
      badge: "/favicon-48x48.png",
      tag: "lle-notification",
      renotify: true,
    };
    if ("serviceWorker" in navigator) {
      const reg = await navigator.serviceWorker.ready;
      await reg.showNotification(title, options);
      return;
    }
    new Notification(title, options);
  } catch { /* notifications unavailable */ }
}

function base64UrlToUint8Array(value) {
  const pad = "=".repeat((4 - (value.length % 4)) % 4);
  const raw = atob((value + pad).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

// Enable closed-app web push: asks permission (user gesture required), subscribes
// via the service worker and stores the subscription on the server per user.
export async function enablePush() {
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) throw new Error("not-supported");
  const permission = await Notification.requestPermission();
  if (permission !== "granted") throw new Error("denied");
  const registration = await navigator.serviceWorker.ready;
  const { data } = await api.get("/push/public-key");
  let subscription = await registration.pushManager.getSubscription();
  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: base64UrlToUint8Array(data.publicKey),
    });
  }
  await api.post("/push/subscribe", subscription.toJSON());
  return subscription;
}

// Re-sync an existing subscription (self-heals after server data loss/migration).
export async function resyncPush() {
  try {
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) return;
    if (typeof Notification === "undefined" || Notification.permission !== "granted") return;
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    if (subscription) await api.post("/push/subscribe", subscription.toJSON());
  } catch { /* best effort */ }
}

export async function disablePush() {
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    if (!subscription) return;
    await api.delete("/push/subscribe", { params: { endpoint: subscription.endpoint } });
    await subscription.unsubscribe();
  } catch { /* best effort */ }
}
